/**
 * Device fingerprint — a stable per-browser identifier for scoping saved map views.
 *
 * Design goals:
 * - Same value across reloads on the same browser profile → your view is remembered.
 * - Different value on a different computer or a different browser profile → each device
 *   has its own saved view (so two computers logged in with the same account don't fight
 *   over the map center).
 * - Opaque string, no personal info, safe to send to the backend.
 */

const STORAGE_KEY = 'ensue_device_fingerprint';

const genUUIDLike = () => {
  // Prefer crypto.randomUUID; fall back to a rough polyfill.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

/** Returns a stable device fingerprint, creating and persisting one on first use. */
export const getDeviceFingerprint = () => {
  try {
    let fp = localStorage.getItem(STORAGE_KEY);
    if (fp && fp.length >= 8) return fp;
    fp = genUUIDLike();
    localStorage.setItem(STORAGE_KEY, fp);
    return fp;
  } catch {
    // In private-mode Safari etc. localStorage may throw; fall back to a
    // per-session random id (still isolated by tab, which is acceptable).
    return genUUIDLike();
  }
};
